import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatDateOrEmpty } from '@helpers/dates';
import { styled } from '@mui/material/styles';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import { MessageStatus, MessageType, SenderType, SentFromLabelKey } from '@constants/inbox';
import { CHANNEL, STATUS } from '@constants/channels';
import { useAccountPeople, useTranslation } from '@hooks';
import { handleApiError } from '@helpers/api';
import { parseMessageMetadata } from '@helpers/inbox';
import { inboxService } from '@services';
import MessageContentWrapper from './MessageContentWrapper';
import MessageSenderAvatar, { SENDER_AVATAR_SIZE } from './MessageSenderAvatar';

const SENDER_AVATAR_GAP = 6;
const SENDER_AVATAR_RAIL = SENDER_AVATAR_SIZE + SENDER_AVATAR_GAP;

const Container = styled('div', {
    shouldForwardProp: prop => prop !== 'type',
})(({ type }) => {
    // Notes are agent-authored, so they sit on the agent (right) side like replies
    const isAgentSide = type === MessageType.Outgoing || type === MessageType.Note;
    return {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 4,
        maxWidth: isAgentSide ? `calc(80% + ${SENDER_AVATAR_RAIL}px)` : '80%',
        alignItems: isAgentSide ? 'flex-end' : 'flex-start',
        alignSelf: isAgentSide ? 'flex-end' : 'flex-start',
        '@media (max-width: 768px)': {},
    };
});

const BubbleRow = styled('div', {
    shouldForwardProp: prop => prop !== 'isOutgoing',
})(({ isOutgoing }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: SENDER_AVATAR_GAP,
    width: '100%',
    justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
}));

const BubbleStack = styled('div', {
    shouldForwardProp: prop => prop !== 'isOutgoing',
})(({ isOutgoing }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: isOutgoing ? 'flex-end' : 'flex-start',
}));

const TextLink = styled(Typography)({
    color: 'var(--text-secondary)',
    marginLeft: '4px',
    cursor: 'pointer',
    '&:hover': {
        color: 'rgba(0, 0, 0, 0.8)',
    },
});

const messageIconStyles = {
    marginLeft: '4px',
    maxWidth: '12px',
    maxHeight: '14px',
};
const SentIcon = styled(DoneIcon)(() => ({ ...messageIconStyles, color: 'var(--text-tertiary)' }));
const DeliveredIcon = styled(DoneAllIcon)(() => ({ ...messageIconStyles, color: 'var(--text-tertiary)' }));
const FailedIcon = styled(ErrorIcon)(() => ({ ...messageIconStyles, color: 'var(--text-secondary)' }));
const ReadIcon = styled(DoneAllIcon)(() => ({ ...messageIconStyles, color: 'var(--primary-main)' }));

const Message = ({ data, agents, locations, assignedLocation, channel, channelStatus, canResend = true }) => {
    const [resendDisabled, setResendDisabled] = useState(false);
    const { t } = useTranslation();
    const { getPersonName } = useAccountPeople();
    const language = useSelector(state => state.auth.user?.language);

    const isOutgoing = data.type === MessageType.Outgoing;
    const isNote = data.type === MessageType.Note;
    const isAgentSide = isOutgoing || isNote;
    const isEmail = channel === CHANNEL.EMAIL;
    const isFailedOutgoing = isOutgoing && data.status === MessageStatus.Failed;
    const deliveryFailure = useMemo(
        () => (isFailedOutgoing ? parseMessageMetadata(data.metadata)?.deliveryFailure : null),
        [isFailedOutgoing, data.metadata],
    );

    const senderDisplayName = useMemo(() => {
        if (data.senderType === SenderType.External) {
            return t(SentFromLabelKey[data.metadata] ?? 'InboxPage.sentFromExternalApp');
        }
        const senderName = getPersonName(data.senderId);
        if (senderName) return senderName;
        if (data.senderAgentId) {
            const agent = agents?.find(a => a.id === data.senderAgentId);
            if (agent) return agent.name;
        }
        return null;
    }, [data.senderType, data.metadata, data.senderId, data.senderAgentId, getPersonName, agents, t]);

    const internalName = useMemo(() => {
        return locations.find(l => l.companyId === assignedLocation)?.internalName;
    }, [assignedLocation, locations]);

    const onClickResendMessage = async () => {
        try {
            setResendDisabled(true);
            await inboxService.resendMessage(data.id);
        } catch (error) {
            handleApiError(error);
        } finally {
            setResendDisabled(false);
        }
    };

    return (
        <Container type={data.type}>
            {isAgentSide && senderDisplayName ? (
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', textAlign: 'right', marginBottom: '4px' }}
                >
                    {senderDisplayName}
                </Typography>
            ) : null}
            {!isAgentSide && channel !== CHANNEL.FACEBOOK && channel !== CHANNEL.INSTAGRAM && assignedLocation ? (
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', textAlign: 'left', marginBottom: '4px' }}
                >
                    {internalName}
                </Typography>
            ) : null}
            <BubbleRow isOutgoing={isAgentSide}>
                {data.children.length === 1 ? (
                    <MessageContentWrapper
                        data={data.children[0]}
                        type={data.type}
                        isLast
                        locations={locations}
                        assignedLocation={assignedLocation}
                        isOutgoing={isOutgoing}
                        isEmail={isEmail}
                        messageStatus={data.status}
                    />
                ) : (
                    <BubbleStack isOutgoing={isAgentSide}>
                        {data.children.map((item, index) => (
                            <MessageContentWrapper
                                key={item.id}
                                data={item}
                                multiple
                                isLast={index === data.children.length - 1}
                                type={data.type}
                                locations={locations}
                                assignedLocation={assignedLocation}
                                isOutgoing={isOutgoing}
                                isEmail={isEmail}
                                messageStatus={data.status}
                            />
                        ))}
                    </BubbleStack>
                )}
                {isAgentSide ? (
                    <MessageSenderAvatar senderId={data.senderId} senderType={data.senderType} channel={channel} />
                ) : null}
            </BubbleRow>
            <div
                style={{
                    display: 'flex',
                    justifyContent: isAgentSide ? 'flex-end' : 'flex-start',
                    alignItems: 'center',
                    marginTop: 4,
                }}
            >
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    {formatDateOrEmpty(data.createdAt, 'd MMM • HH:mm', language)}
                </Typography>
                {isFailedOutgoing ? (
                    <Tooltip title={deliveryFailure || ''} placement="top">
                        <Typography
                            variant="caption"
                            tabIndex={deliveryFailure ? 0 : undefined}
                            sx={{ color: 'var(--text-secondary)', marginLeft: '4px' }}
                        >
                            {t('common.failed')}
                        </Typography>
                    </Tooltip>
                ) : null}
                {isOutgoing && data.status === MessageStatus.Sent ? <SentIcon /> : null}
                {isOutgoing && data.status === MessageStatus.Delivered ? <DeliveredIcon /> : null}
                {isFailedOutgoing ? <FailedIcon /> : null}
                {isOutgoing && data.status === MessageStatus.Read ? <ReadIcon /> : null}
                {canResend && !resendDisabled && isFailedOutgoing && channelStatus !== STATUS.DISCONNECTED ? (
                    <TextLink variant="caption" onClick={onClickResendMessage}>
                        {t('InboxPage.resend')}
                    </TextLink>
                ) : null}
            </div>
        </Container>
    );
};

export default Message;
